import { Menu, Popup } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reducer/store";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { api, ui, utils } from "services";
import { useEffect, useState } from "react";
import { CustomerInterface, LoginType, Size } from "interfaces";
import { clearUserInfo } from "reducer/user.slice";
import { clearAuthInfos } from "reducer/wallet.slice";
import { useLocation } from "react-router-dom";
import { MenuIcon } from "@heroicons/react/outline";
import { PageComponent } from "components/page-component/page-component";
import { ExtensionLinkComponent } from "components/extension";
import { SSO_META } from "config";
import { SingleSignon } from "components/singlesignon/singlesignon";

export default function Header() {
  const dispatch = useDispatch();
  const location = useLocation();
  const nav = useNavigate();
  const path = location.pathname.split("/")[1];

  const user = useSelector((state: RootState) => state.user);
  const wallets = useSelector((state: RootState) => state.wallets);
  const authInfos = useSelector((state: RootState) => state.wallets);
  const loginType = useSelector((state: RootState) => state.logintype);

  const [clickedLogout, setClickedLogout] = useState(false);
  const [addressComponent, setAddress] = useState(<></>);

  async function onLogoutClick() {
    setClickedLogout(true);
    await ui.confirm(
      t("Are you sure want to logout?"),
      () => setClickedLogout(false),
      () => setClickedLogout(false)
    );
    dispatch(clearUserInfo());
    dispatch(clearAuthInfos());
    sessionStorage.clear();
    localStorage.clear();
    nav("/");
  }

  useEffect(() => {
    setAddress(
      <>{utils.displayWalletAddress(utils.getAddressDisplay(wallets))}</>
    );
  }, [wallets, loginType]);

  return (
    <div className="w-full main-header bg-black-primary header-container">
      <div className="hidden xl:block">
        <Menu
          inverted={true}
          className="main-container"
          borderless
          key="leftmenu"
        >
          <Menu.Item
            onClick={() => {
              nav("/");
            }}
            header
            className="logo-header"
            key="logo"
          >
            <img src="/assets/images/logo.png" alt="logo" />
          </Menu.Item>
          <PageComponent SSO_META={SSO_META()} />
          <Menu.Item
            key="account"
            onClick={() => {
              nav("/account");
            }}
            active={["account"].includes(path)}
          >
            <p>My Account</p>
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              nav("/");
            }}
            key="treasury"
            active={["treasury", ""].includes(path)}
          >
            <p>Treasury</p>
          </Menu.Item>
          <Menu.Item
            active={["referral"].includes(path)}
            onClick={() => {
              nav("/referral");
            }}
            key="referral"
          >
            <p>Referral</p>
          </Menu.Item>
          <Menu.Item
            active={["raffle"].includes(path)}
            onClick={() => {
              nav("/raffle");
            }}
            key="raffle"
          >
            <p>Raffle</p>
          </Menu.Item>
          <Menu.Item
            active={["mag-vault"].includes(path)}
            onClick={() => {
              nav("/mag-vault");
            }}
            key="mag-vault"
          >
            <p>MAG Vault</p>
          </Menu.Item>
          <Menu.Item
            onClick={async () => {
            }}
            className="link-disabled" key="govern">
            <p>Govern</p>
          </Menu.Item>
          <Menu.Menu
            inverted={true.toString()}
            position="right"
            key="rightmenu"
          >
            {user?.[CustomerInterface.id] && (
              <Menu.Item className="hoverless" key="popup-login">
                <div className="rounded-lg border border-grey-primary overflow-hidden grid grid-cols-10 w-[400px]">
                  <Popup
                    style={{ padding: "unset", border: "unset" }}
                    hoverable
                    trigger={
                      <div className="text-center col-span-4 bg-black-linear-4 flex px-3 pt-0.5">
                        <div className="mt-4">{addressComponent}</div>
                        <img
                          src="/assets/images/icons/select-icon.svg"
                          alt="select"
                          className="ml-auto h-8 w-8 mt-2"
                        />
                      </div>
                    }
                    content={
                      clickedLogout ? (
                        <></>
                      ) : (
                        <>
                          <div className="w-[600px] max-w-full py-4 bg-black-linear-secondary">
                            <div className="text-white mb-4 px-10 ">
                              Your wallet
                            </div>
                            <ExtensionLinkComponent
                              authInfos={authInfos}
                              size={Size.Small}
                            />
                          </div>
                          <span
                            className="flex bg-black-linear-3 py-4 px-10 text-white-light cursor-pointer hover-orange"
                            onClick={onLogoutClick}
                          >
                            <img
                              src="/assets/images/icons/logout-icon.svg"
                              alt="logout"
                              className="h-6 w-6 mr-2"
                            />{" "}
                            Logout
                          </span>
                        </>
                      )
                    }
                    basic
                  />
                  <div className="text-center col-span-6 grid grid-cols-11 bg-black-linear py-3 px-3">
                    <div className="col-span-5 mr-auto">
                      <img
                        src="/assets/images/items/mstr-icon.svg"
                        alt="mstr"
                        className="inline-block mr-1"
                      />
                      <span className="secondary-font-medium text-sm">
                        {utils.convertToInternationalCurrencySystem(
                          user.mstr_wallet
                        ) ?? "--"}
                      </span>
                    </div>
                    <div
                      className="vr col-span-1 mt-3"
                      style={{ height: "1px" }}
                    ></div>
                    <div className="col-span-5 mr-auto text-sm">
                      <img
                        src="/assets/images/items/mag-icon.webp"
                        alt="mag"
                        className="inline-block mr-1"
                      />
                      <span className="secondary-font-medium text-sm">
                        {utils.convertToInternationalCurrencySystem(
                          user.mag_wallet
                        ) ?? "--"}
                      </span>
                    </div>
                  </div>
                </div>
              </Menu.Item>
            )}
            {!user?.[CustomerInterface.id] && (
              <>
                <Menu.Item
                  className="hoverless"
                  id="popup-signup"
                  key="popup signup"
                >
                  <SingleSignon type="signup" device="laptop" />
                </Menu.Item>

                <Menu.Item className="hoverless" key="popup signin">
                  <SingleSignon type="signin" device="laptop" />
                </Menu.Item>
              </>
            )}
          </Menu.Menu>
        </Menu>
      </div>

      <div className="xl:hidden w-full">
        <Menu inverted={true} borderless className="header-mb">
          <Menu.Item
            onClick={() => {
              nav("/");
            }}
            header
            className="logo-header"
            key="mb-logo"
          >
            <img src="/assets/images/logo.png" alt="logo" />
          </Menu.Item>
          {/* <PageComponent SSO_META={SSO_META()} /> */}
          {user?.[CustomerInterface.id] ? (
            <Menu.Item position="right" key="mb-item-menu">
              <Popup
                hoverable
                position="bottom right"
                style={{
                  padding: "unset",
                  border: "unset",
                  transform: "translateX(-30px)",
                  maxWidth: "100vw",
                }}
                trigger={
                  <div className="text-center col-span-4 bg-black-linear-4 flex px-3 pt-0.5">
                    <MenuIcon className="w-7 h-8 mb-1" />
                  </div>
                }
                content={
                  clickedLogout ? (
                    <></>
                  ) : (
                    <>
                      <div className="w-screen self-center max-w-[600px] py-4 px-10 bg-black-linear-secondary fixed relative top-0">
                        <Menu
                          inverted={true}
                          className="navbar-md flex flex-wrap"
                          borderless
                          key="mb-menu"
                        >
                          <Menu.Item
                            key="mb-account"
                            onClick={() => {
                              nav("/account");
                            }}
                            active={["account"].includes(path)}
                          >
                            <p>My Account</p>
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => {
                              nav("/");
                            }}
                            key="mb-treasury"
                            active={["treasury", ""].includes(path)}
                          >
                            <p>Treasury</p>
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => {
                              nav("/referral");
                            }}
                            key="mb-referral"
                            className=""
                            active={["referral"].includes(path)}
                          >
                            <p>Referral</p>
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => {
                              nav("/raffle");
                            }}
                            key="mb-raffle"
                            className=""
                            active={["raffle"].includes(path)}
                          >
                            <p>Raffle</p>
                          </Menu.Item>
                          <Menu.Item
                            active={["mag-vault"].includes(path)}
                            onClick={() => {
                              nav("/mag-vault");
                            }}
                            key="mag-vault"
                          >
                            <p>MAG Vault</p>
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => { }}
                            className="link-disabled"
                            key="mb-Govern"
                          >
                            <p>Govern</p>
                          </Menu.Item>
                        </Menu>
                        {user?.[CustomerInterface.id] && (
                          <>
                            <div className="text-center grid grid-cols-11 bg-black-linear py-3 px-3 text-white">
                              <div className="col-span-5">
                                <img
                                  src="/assets/images/items/mstr-icon.svg"
                                  alt="mstr"
                                  className="inline-block mr-2"
                                />
                                <span className="secondary-font-medium text-sm">
                                  {utils.convertToInternationalCurrencySystem(
                                    user?.mstr_wallet
                                  ) ?? "--"}
                                </span>
                              </div>
                              <div
                                className="vr col-span-1 mt-3"
                                style={{ height: "1px" }}
                              ></div>
                              <div className="col-span-5 text-sm">
                                <img
                                  src="/assets/images/items/mag-icon.webp"
                                  alt="mag"
                                  className="inline-block mr-2"
                                />
                                <span className="secondary-font-medium">
                                  {utils.convertToInternationalCurrencySystem(
                                    user?.mag_wallet
                                  ) ?? "--"}
                                </span>
                              </div>
                            </div>

                            <div className="text-white mt-5">Your wallet</div>

                            <ExtensionLinkComponent
                              authInfos={authInfos}
                              size={Size.Small}
                            />
                          </>
                        )}
                      </div>
                      {user?.[CustomerInterface.id] && (
                        <div className="flex justify-between bg-black-linear-3 py-4 px-10 ">
                          <span
                            className="flex mt-4 text-white-light cursor-pointer hover-orange"
                            onClick={onLogoutClick}
                          >
                            <img
                              src="/assets/images/icons/logout-icon.svg"
                              alt="logout"
                              className="h-6 w-6 mr-2"
                            />{" "}
                            Logout
                          </span>
                        </div>
                      )}
                    </>
                  )
                }
                basic
              />
            </Menu.Item>
          ) : (
            <>
              <Menu.Item position="right" className="hoverless" key="mbsignup">
                <SingleSignon type="signup" device="mobile" />
              </Menu.Item>
              <Menu.Item className="hoverless" key="mbsignin">
                <SingleSignon type="login" device="mobile" />
              </Menu.Item>
            </>
          )}
        </Menu>
      </div>
    </div>
  );
}
